import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { supabase } from '../../lib/supabase';
import { User } from '@supabase/supabase-js';
import { Profile, fetchUserProfile, clearProfileCache } from '../../lib/profileQueries';

export interface AuthState {
  user: User | null;
  session: any;
  profile: Profile | null;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  session: null,
  profile: null,
  loading: true,
  error: null
};

// Función de prueba para validar la estructura de la tabla
const validateTableStructure = async () => {
  try {
    const { data, error } = await supabase()
      .from('profiles')
      .select('*')
      .limit(1);

    if (error) {
      console.error('Error al validar estructura:', error);
      throw error;
    }

    console.log('Estructura de la tabla profiles:', data);
    return data;
  } catch (error) {
    console.error('Error en validación:', error);
    throw error;
  }
};

export const fetchSession = createAsyncThunk(
  'auth/fetchSession',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      console.log('Iniciando fetchSession...');
      const { data: { session }, error: sessionError } = await supabase().auth.getSession();
      console.log('Resultado de getSession:', { session, sessionError });

      if (sessionError) {
        console.error('Error al obtener sesión:', sessionError);
        dispatch(setLoading(false));
        return rejectWithValue(sessionError.message);
      }

      if (!session) {
        console.log('No hay sesión de usuario');
        dispatch(setLoading(false));
        return { session: null, profile: null };
      }

      // Verificar si el usuario está autenticado
      if (!session.user) {
        console.log('No hay usuario en la sesión');
        dispatch(setLoading(false));
        return { session: null, profile: null };
      }

      console.log('Obteniendo perfil del usuario:', session.user.id);
      const profile = await fetchUserProfile(session.user.id);

      console.log('Retornando datos formateados:', { session, profile });
      dispatch(setLoading(false));
      return { session, profile };
    } catch (error) {
      console.error('Error en fetchSession:', error);
      dispatch(setLoading(false));
      return rejectWithValue(error instanceof Error ? error.message : 'Error desconocido');
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData: Partial<Profile>) => {
    const { data: { user } } = await supabase().auth.getUser();
    if (!user) throw new Error('No hay usuario autenticado');

    // Actualizar el perfil
    const { data: profile, error } = await supabase()
      .from('profiles')
      .update(profileData)
      .eq('id', user.id)
      .select()
      .single();

    if (error) throw error;

    const formattedProfile: Profile = {
      id: String(profile.id),
      role: String(profile.role),
      otp_validated: Boolean(profile.otp_validated),
      credits: Number(profile.credits) || 0,
      email: String(profile.email),
      first_name: String(profile.first_name),
      last_name: String(profile.last_name),
      full_name: String(profile.full_name),
      phone: String(profile.phone),
      professional_card: String((profile as any).professional_card || ''),
      company_name: String((profile as any).company_name || ''),
      company_email: String((profile as any).company_email || ''),
      company_phone: String((profile as any).company_phone || ''),
      logo_image: (profile as any).logo_image || null,
      signature_image: (profile as any).signature_image || null
    };

    return formattedProfile;
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuth: (state) => {
      console.log('🧹 clearAuth ejecutándose - Estado anterior:', { ...state });
      state.user = null;
      state.session = null;
      state.profile = null;
      state.error = null;
      state.loading = false;
      clearProfileCache();
    },
    setLoading: (state, action) => {
      console.log('Cambiando estado de loading a:', action.payload);
      state.loading = action.payload;
    },
    setSession: (state, action) => {
      console.log('Estableciendo sesión:', action.payload);
      state.session = action.payload;
      state.user = action.payload?.user ?? null;
      state.loading = false;
    },
    updateProfileCredits: (state, action) => {
      console.log('Actualizando créditos en Redux:', action.payload);
      if (state.profile) {
        state.profile.credits = action.payload;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSession.pending, (state) => {
        console.log('fetchSession pending - Estado actual:', { ...state });
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSession.fulfilled, (state, action) => {
        console.log('fetchSession fulfilled - Payload:', action.payload);
        state.session = action.payload.session;
        state.user = action.payload.session?.user ?? null;
        state.profile = action.payload.profile;
        state.error = null;
        console.log('Estado actualizado:', { ...state });
      })
      .addCase(fetchSession.rejected, (state, action) => {
        console.error('fetchSession rejected:', action.error);
        state.error = action.payload as string;
        state.user = null;
        state.session = null;
        state.profile = null;
        console.log('Estado después del error:', { ...state });
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        console.log('updateUserProfile fulfilled:', action.payload);
        state.profile = action.payload;
      });
  }
});

export const { clearAuth, setLoading, setSession, updateProfileCredits } = authSlice.actions;
export default authSlice.reducer; 